import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { PlusCircleIcon, TrashIcon, PencilIcon, MagnifyingGlassIcon, ClipboardDocumentIcon, ClipboardIcon } from "@heroicons/react/24/outline"

const ColorTableEditor = ({
  value = {},
  onChange,
  label,
  description,
  variant = "admin",
  readOnly = false,
}) => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)
  const [editingColor, setEditingColor] = useState(null)
  const [searchKey, setSearchKey] = useState("")
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [colorInputRef, setColorInputRef] = useState(null)

  // Handle different data formats
  const normalizeValue = (val) => {
    if (!val) return {}
    
    // If it's already an object, return it
    if (typeof val === 'object' && !Array.isArray(val)) {
      return val
    }
    
    // If it's an array of objects with key/value properties
    if (Array.isArray(val)) {
      const obj = {}
      val.forEach(item => {
        if (item && typeof item === 'object' && item.key) {
          obj[item.key] = item.value || ''
        }
      })
      return obj
    }
    
    return {}
  }

  // Convert RGB space-separated to hex for color input
  const rgbToHex = (rgb) => {
    if (!rgb || typeof rgb !== 'string') return "#000000"
    
    const trimmed = rgb.trim()
    
    // Handle hex colors (including 8-digit with alpha)
    if (trimmed.startsWith('#')) {
      // For 8-digit hex (with alpha), convert to 6-digit for color picker compatibility
      if (trimmed.length === 9) {
        return trimmed.substring(0, 7) // Remove alpha channel for color picker
      }
      return trimmed
    }
    
    // Handle RGB space-separated format (e.g., "255 0 0")
    const parts = trimmed.split(' ').map(p => parseInt(p.trim()))
    if (parts.length === 3 && parts.every(p => !isNaN(p) && p >= 0 && p <= 255)) {
      return `#${parts.map(p => p.toString(16).padStart(2, '0')).join('')}`
    }
    
    return "#000000"
  }

  // Comprehensive CSS color validation
  const isValidCSSColor = (color) => {
    if (!color || typeof color !== 'string') return false
    
    const trimmed = color.trim()
    
    // Hex colors (#fff, #ffffff, fff, ffffff)
    const hexRegex = /^#?([a-f\d]{3}|[a-f\d]{6}|[a-f\d]{8})$/i
    if (hexRegex.test(trimmed)) return true
    
    // RGB/RGBA colors
    const rgbRegex = /^rgba?\(\s*(\d+(?:\.\d+)?(?:%)?)(\s*,\s*(\d+(?:\.\d+)?(?:%)?)){2}(\s*,\s*(0|1|0?\.\d+))?\s*\)$/i
    if (rgbRegex.test(trimmed)) return true
    
    // HSL/HSLA colors
    const hslRegex = /^hsla?\(\s*(\d+(?:\.\d+)?)(\s*,\s*(\d+(?:\.\d+)?%))(\s*,\s*(\d+(?:\.\d+)?%))(\s*,\s*(0|1|0?\.\d+))?\s*\)$/i
    if (hslRegex.test(trimmed)) return true
    
    // CSS Variables
    if (/^var\(\s*--[a-zA-Z][a-zA-Z0-9-_]*\s*(,\s*.+)?\)$/i.test(trimmed)) return true
    
    // Named colors (comprehensive list of CSS named colors)
    const namedColors = [
      'transparent', 'aliceblue', 'antiquewhite', 'aqua', 'aquamarine', 'azure', 'beige', 'bisque', 'black', 'blanchedalmond',
      'blue', 'blueviolet', 'brown', 'burlywood', 'cadetblue', 'chartreuse', 'chocolate', 'coral', 'cornflowerblue',
      'cornsilk', 'crimson', 'cyan', 'darkblue', 'darkcyan', 'darkgoldenrod', 'darkgray', 'darkgrey', 'darkgreen',
      'darkkhaki', 'darkmagenta', 'darkolivegreen', 'darkorange', 'darkorchid', 'darkred', 'darksalmon', 'darkseagreen',
      'darkslateblue', 'darkslategray', 'darkslategrey', 'darkturquoise', 'darkviolet', 'deeppink', 'deepskyblue',
      'dimgray', 'dimgrey', 'dodgerblue', 'firebrick', 'floralwhite', 'forestgreen', 'fuchsia', 'gainsboro', 'ghostwhite',
      'gold', 'goldenrod', 'gray', 'grey', 'green', 'greenyellow', 'honeydew', 'hotpink', 'indianred', 'indigo', 'ivory',
      'khaki', 'lavender', 'lavenderblush', 'lawngreen', 'lemonchiffon', 'lightblue', 'lightcoral', 'lightcyan',
      'lightgoldenrodyellow', 'lightgray', 'lightgrey', 'lightgreen', 'lightpink', 'lightsalmon', 'lightseagreen',
      'lightskyblue', 'lightslategray', 'lightslategrey', 'lightsteelblue', 'lightyellow', 'lime', 'limegreen', 'linen',
      'magenta', 'maroon', 'mediumaquamarine', 'mediumblue', 'mediumorchid', 'mediumpurple', 'mediumseagreen',
      'mediumslateblue', 'mediumspringgreen', 'mediumturquoise', 'mediumvioletred', 'midnightblue', 'mintcream',
      'mistyrose', 'moccasin', 'navajowhite', 'navy', 'oldlace', 'olive', 'olivedrab', 'orange', 'orangered', 'orchid',
      'palegoldenrod', 'palegreen', 'paleturquoise', 'palevioletred', 'papayawhip', 'peachpuff', 'peru', 'pink', 'plum',
      'powderblue', 'purple', 'red', 'rosybrown', 'royalblue', 'saddlebrown', 'salmon', 'sandybrown', 'seagreen',
      'seashell', 'sienna', 'silver', 'skyblue', 'slateblue', 'slategray', 'slategrey', 'snow', 'springgreen', 'steelblue',
      'tan', 'teal', 'thistle', 'tomato', 'turquoise', 'violet', 'wheat', 'white', 'whitesmoke', 'yellow', 'yellowgreen',
      'currentcolor', 'inherit', 'initial', 'unset', 'revert', 'revert-layer'
    ]
    
    if (namedColors.includes(trimmed.toLowerCase())) return true
    
    return false
  }
  
  // Legacy function for backward compatibility
  const isValidHex = (hex) => {
    if (!hex) return false
    const hexRegex = /^#?([a-f\d]{3}|[a-f\d]{6}|[a-f\d]{8})$/i
    return hexRegex.test(hex)
  }

  // Convert any CSS color to normalized format for storage
  const normalizeColorForStorage = (color) => {
    if (!color || typeof color !== 'string') return color
    
    const trimmed = color.trim()
    
    // For hex colors, convert to RGB space-separated (legacy format)
    if (isValidHex(trimmed)) {
      const normalizedHex = trimmed.replace(/^#/, '')
      let fullHex = normalizedHex
      
      // Handle shorthand hex (#RGB to #RRGGBB)
      if (normalizedHex.length === 3) {
        fullHex = normalizedHex.split('').map(char => char + char).join('')
      }
      
      const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex)
      if (result) {
        return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
      }
    }
    
    // For all other CSS color formats, store as-is
    return trimmed
  }
  

  const handleColorClick = (colorKey) => {
    const currentValue = normalizeValue(value)
    const currentColorValue = currentValue[colorKey] || "#000000"
    
    // Convert RGB to hex for color picker
    const hexValue = rgbToHex(currentColorValue)
    
    setEditingColor({
      key: colorKey,
      value: hexValue
    })
    setIsAddingNew(false)
    setIsColorPickerOpen(true)
  }

  const handleAddColorClick = () => {
    setEditingColor({
      key: "",
      value: "#000000"
    })
    setIsAddingNew(true)
    setIsColorPickerOpen(true)
  }

  const handleColorSave = () => {
    if (!editingColor) return
    
    // Validate CSS color format
    if (!isValidCSSColor(editingColor.value)) {
      showNotification({
        message: "Invalid color format. Supported formats: hex (#FF0000), rgb(255,0,0), hsl(0,100%,50%), named colors (red, blue), CSS variables (var(--color))",
        className: "bg-admin_error text-admin_text_inv",
      })
      return
    }
    
    // For adding new colors, key must be provided and unique
    if (isAddingNew) {
      if (!editingColor.key.trim()) {
        showNotification({
          message: "Color name is required",
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }
      
      const currentValue = normalizeValue(value)
      if (currentValue[editingColor.key.trim()]) {
        showNotification({
          message: `Color "${editingColor.key.trim()}" already exists`,
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }
    }
    
    const currentValue = normalizeValue(value)
    const newValue = { ...currentValue }
    const colorKey = isAddingNew ? editingColor.key.trim() : editingColor.key
    newValue[colorKey] = normalizeColorForStorage(editingColor.value)
    
    onChange(newValue)
    setIsColorPickerOpen(false)
    setEditingColor(null)
    setIsAddingNew(false)
    
    // Show success notification
    showNotification({
      message: isAddingNew ? `Color "${colorKey}" added successfully` : `Color "${colorKey}" updated successfully`,
      className: "bg-admin_success text-admin_text_inv",
    })
  }


  const handleDeleteColor = (colorKey) => {
    const currentValue = normalizeValue(value)
    const newValue = { ...currentValue }
    delete newValue[colorKey]
    onChange(newValue)
  }

  // Copy color table to clipboard
  const handleCopyColorTable = async () => {
    try {
      const currentValue = normalizeValue(value)
      const copyData = {
        __colorTableMarker: "SiteTheme_ColorTable_v1", // Verification marker
        colors: currentValue,
        timestamp: new Date().toISOString()
      }
      
      await navigator.clipboard.writeText(JSON.stringify(copyData, null, 2))
      
      showNotification({
        message: `${Object.keys(currentValue).length} colors copied to clipboard`,
        className: "bg-admin_success text-admin_text_inv",
      })
    } catch (error) {
      showNotification({
        message: "Failed to copy colors to clipboard",
        className: "bg-admin_error text-admin_text_inv",
      })
    }
  }

  // Paste color table from clipboard
  const handlePasteColorTable = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText()
      
      if (!clipboardText.trim()) {
        showNotification({
          message: "Clipboard is empty",
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }

      let pasteData
      try {
        // Clean up common paste issues:
        // 1. Replace smart quotes with straight quotes
        // 2. Remove trailing commas before closing braces/brackets
        const cleanedText = clipboardText
          .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')  // Replace smart double quotes
          .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")  // Replace smart single quotes
          .replace(/,(\s*[}\]])/g, '$1')  // Remove trailing commas
        pasteData = JSON.parse(cleanedText)
      } catch (parseError) {
        showNotification({
          message: "Invalid data format in clipboard",
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }

      // Verify it's a valid color table copy
      if (!pasteData.__colorTableMarker || pasteData.__colorTableMarker !== "SiteTheme_ColorTable_v1") {
        showNotification({
          message: "Clipboard doesn't contain a valid color table",
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }

      if (!pasteData.colors || typeof pasteData.colors !== 'object') {
        showNotification({
          message: "Invalid color data in clipboard",
          className: "bg-admin_error text-admin_text_inv",
        })
        return
      }

      // Apply the pasted colors (removing the marker)
      const { __colorTableMarker, timestamp, ...cleanData } = pasteData
      onChange(cleanData.colors)
      
      showNotification({
        message: `${Object.keys(cleanData.colors).length} colors pasted successfully`,
        className: "bg-admin_success text-admin_text_inv",
      })
    } catch (error) {
      showNotification({
        message: "Failed to read clipboard data",
        className: "bg-admin_error text-admin_text_inv",
      })
    }
  }

  const getColorPreview = (colorValue) => {
    if (!colorValue) return "#cccccc"
    
    const colorStr = String(colorValue).trim()
    
    // Handle RGB space-separated (legacy format)
    if (/^\d+\s+\d+\s+\d+$/.test(colorStr)) {
      return rgbToHex(colorStr)
    }
    
    // For all other CSS color formats, return as-is and let CSS handle it
    // The browser will render these correctly in the preview
    if (isValidCSSColor(colorStr)) {
      return colorStr
    }
    
    return "#cccccc"
  }

  const normalizedValue = normalizeValue(value)
  const colorEntries = Object.entries(normalizedValue)
  
  // Filter colors based on search
  const filteredEntries = colorEntries.filter(([colorKey]) => {
    if (!searchKey.trim()) return true
    return colorKey.toLowerCase().includes(searchKey.toLowerCase())
  })

  return (
    <div className="space-y-4">
      {label && (
        <Typography variant="admin_label" className="font-medium">
          {label}
        </Typography>
      )}
      
      {description && (
        <Typography variant="admin_caption" className="text-admin_text_secondary">
          {description}
        </Typography>
      )}

      {readOnly && (
        <div className="flex items-center gap-2 px-3 py-2 rounded bg-admin_neutral_1 border border-admin_bordered text-sm text-admin_text_secondary">
          <span>Read-only — colors are defined in code and synced automatically.</span>
        </div>
      )}

      {/* Search Input */}
      <div className="mb-4 flex items-center justify-between">
        <Input
          variant="admin"
          placeholder="Search color keys..."
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          leftIcon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />}
          className="max-w-sm"
        />
        <div className="flex items-center gap-2">
          {colorEntries.length > 0 && (
            <Typography variant="admin_caption" className="text-admin_text_secondary">
              {searchKey ? `${filteredEntries.length} of ${colorEntries.length} colors` : `${colorEntries.length} colors`}
            </Typography>
          )}
          <div className="flex items-center gap-1">
            <Tooltip text="Copy color table to clipboard">
              <Button
                variant="admin_secondary"
                onClick={handleCopyColorTable}
                className="text-sm p-2 h-auto rounded-full"
                disabled={colorEntries.length === 0}
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
              </Button>
            </Tooltip>
            {!readOnly && (
              <>
                <Tooltip text="Paste color table from clipboard">
                  <Button
                    variant="admin_secondary"
                    onClick={handlePasteColorTable}
                    className="text-sm p-2 h-auto rounded-full"
                  >
                    <ClipboardIcon className="w-4 h-4" />
                  </Button>
                </Tooltip>
                <Button
                  variant="admin_primary"
                  onClick={handleAddColorClick}
                  title="Add new color"
                  className="text-sm px-3 py-1.5 h-auto"
                >
                  <PlusCircleIcon className="w-4 h-4 mr-1" />
                  Add Color
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Color Table */}
      <div className="border border-admin_bordered rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-admin_neutral_1">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-admin_text">Preview</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-admin_text">Color Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-admin_text">Value</th>
              {!readOnly && <th className="px-4 py-3 text-left text-sm font-medium text-admin_text">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-admin_bordered">
            {filteredEntries.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-admin_text_secondary">
                  {colorEntries.length === 0 ? "No colors defined. Add your first color below." : "No colors match your search."}
                </td>
              </tr>
            ) : (
              filteredEntries.map(([colorKey, colorValue]) => (
                <tr key={String(colorKey)} className="hover:bg-admin_neutral_1">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded border border-admin_bordered cursor-pointer hover:scale-110 transition-transform"
                        style={{ backgroundColor: getColorPreview(colorValue) }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleColorClick(colorKey)
                        }}
                        title="Click to preview color"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Typography
                      variant="admin_body"
                      className="font-medium cursor-pointer hover:text-admin_primary hover:underline"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleColorClick(colorKey)
                      }}
                      title="Click to preview color"
                    >
                      {String(colorKey)}
                    </Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="admin_body" className="font-mono text-sm">
                      {colorValue}
                    </Typography>
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="admin_icon"
                          onClick={() => handleDeleteColor(colorKey)}
                          className="text-admin_error hover:bg-admin_error hover:text-admin_text_inv"
                          title="Delete color"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>


      {/* Color Picker Modal */}
      <Modal
        isOpen={isColorPickerOpen}
        onClose={() => {
          setIsColorPickerOpen(false)
          setEditingColor(null)
          setIsAddingNew(false)
        }}
        variant="admin_centered"
        className="w-auto"
        closeOnEscape={false}
      >
        <div className="p-4 pt-0 space-y-10">
          <div className="flex items-center gap-3">
            <Typography variant="admin_h3">
              {isAddingNew ? "Add New Color" : editingColor?.key}
            </Typography>
            {readOnly && (
              <span className="px-2 py-0.5 rounded text-xs bg-admin_neutral_1 text-admin_text_secondary border border-admin_bordered">
                Read only
              </span>
            )}
          </div>
          
          <div className="space-y-8">
            {/* Color name input for new colors */}
            {isAddingNew && (
              <div>
                <Typography 
                  variant="admin_txt1" 
                  className="block text-admin_text mb-1"
                >
                  Color Name
                </Typography>

                <Input
                  variant="admin"
                  type="text"
                  placeholder="Color name (e.g., primary, text_color)"
                  value={editingColor?.key || ""}
                  onChange={(e) => setEditingColor(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full"
                />
              </div>
            )}
            
            <div className="flex items-start gap-4">
              {/* Color Preview Patch */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-16 h-16 rounded-lg border border-admin_bordered shadow-sm mb-1.5 ${readOnly ? "cursor-default" : "cursor-pointer hover:scale-105 transition-transform"}`}
                  style={{ backgroundColor: editingColor?.value || "#000000" }}
                  onClick={() => !readOnly && colorInputRef?.click()}
                  title={readOnly ? undefined : "Click to open color picker"}
                />
                {!readOnly && (
                  <Typography variant="admin_txt2_dim">(click to edit)</Typography>
                )}
              </div>

              {/* Hidden color input */}
              {!readOnly && (
                <input
                  ref={setColorInputRef}
                  type="color"
                  value={editingColor?.value || "#000000"}
                  onChange={(e) => setEditingColor(prev => ({ ...prev, value: e.target.value }))}
                  className="sr-only"
                />
              )}

              {/* CSS Color Input */}
              <div className="flex-1">
                <Typography
                  variant="admin_txt1"
                  className="block text-admin_text mb-1"
                >
                  CSS Color Value
                </Typography>

                <Input
                  variant="admin"
                  type="text"
                  value={editingColor?.value || "#000000"}
                  onChange={(e) => !readOnly && setEditingColor(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="#FF0000, rgb(255,0,0), hsl(0,100%,50%), red, var(--primary)"
                  className="w-full font-mono"
                  readOnly={readOnly}
                />
              </div>
            </div>
            
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="admin_secondary"
              onClick={() => {
                setIsColorPickerOpen(false)
                setEditingColor(null)
                setIsAddingNew(false)
              }}
              className="flex-1"
            >
              Close
            </Button>

            {!readOnly && (
              <Button
                variant="admin_primary"
                onClick={handleColorSave}
                className="flex-1"
                disabled={isAddingNew && !editingColor?.key?.trim()}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ColorTableEditor